/**
 * Constants for the file autofill + delivery feature.
 */

// ── File delivery format ─────────────────────────────────────────────────────

/**
 * Which filled file format(s) get attached to the delivery email. Only meaningful
 * for Word (`docx`) templates — a PDF template can only produce a PDF.
 * - `both`: editable `.docx` + flattened `.pdf` (default)
 * - `pdf`: `.pdf` only
 * - `docx`: editable `.docx` only (falls back to `.pdf` when no docx is available)
 */
export type FilledFilesFormat = "both" | "pdf" | "docx";

/** Default attached-format when a form hasn't chosen one. */
export const DEFAULT_FILLED_FILES_FORMAT: FilledFilesFormat = "both";

/**
 * Coerces an untrusted value (e.g. read from the `email_templates` JSONB, which
 * is not type-checked at runtime) into a valid `FilledFilesFormat`, falling back
 * to the default for anything outside the allowlist.
 *
 * @param value - Raw stored/forwarded value of unknown shape
 * @returns A guaranteed-valid `FilledFilesFormat`
 */
export function coerceFilledFilesFormat(value: unknown): FilledFilesFormat {
  return value === "pdf" || value === "docx" || value === "both" ? value : DEFAULT_FILLED_FILES_FORMAT;
}

/**
 * Decides which filled file format(s) to surface for a given delivery choice —
 * shared by the delivery email (which files to attach) and the confirmation
 * screens (which download buttons to show), so the two never drift.
 *
 * The PDF is always generated, so it acts as the safety net: it's offered
 * whenever the DOCX isn't, including for any unexpected `attachFormat` value.
 *
 * @param input.attachFormat - The form's configured delivery format
 * @param input.hasDocx - Whether an editable `.docx` is available for this response
 * @returns Which of the two files to offer (`wantPdf` is never both-false with `wantDocx`)
 */
export function selectFilledFileFormats(input: { attachFormat: FilledFilesFormat; hasDocx: boolean }): {
  wantPdf: boolean;
  wantDocx: boolean;
} {
  const { attachFormat, hasDocx } = input;
  const wantDocx = hasDocx && (attachFormat === "both" || attachFormat === "docx");
  const wantPdf = !wantDocx || attachFormat === "both";
  return { wantPdf, wantDocx };
}

// ── Storage Paths ────────────────────────────────────────────────────────────

/** Supabase storage bucket for form files. */
export const FORMS_BUCKET = "forms";

/**
 * Storage path template for PDF templates.
 * Pattern: `{formId}/pdf-template/{filename}`
 */
export const PDF_TEMPLATE_PATH_PREFIX = "pdf-template";

/**
 * Storage path template for filled PDFs.
 * Pattern: `{formId}/filled/{responseId}.pdf`
 */
export const FILLED_FILE_PATH_PREFIX = "filled";

// ── Email Defaults ───────────────────────────────────────────────────────────

/** Sender address for filled PDF emails. */
export const FILE_DELIVERY_EMAIL_FROM = "events@nowadays.ai";

/** Sender name for filled PDF emails. */
export const FILE_DELIVERY_EMAIL_FROM_NAME = "Nowadays Events";

/** Default email subject when form doesn't specify custom subject. */
export const FILE_DELIVERY_EMAIL_DEFAULT_SUBJECT = "Your completed form";

/** Escapes a string for safe interpolation into HTML. */
function escapeHtml(input: string): string {
  return input.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

/**
 * Builds the default email body when a form doesn't specify a custom body.
 * Uses the recipient's first name in the greeting when available; falls back
 * to "Hi there," otherwise.
 *
 * @param formName - Display name of the form (HTML-escaped before interpolation)
 * @param firstName - Recipient's first name (optional)
 * @returns HTML body for the email
 */
export function buildDefaultFileDeliveryEmailBody(formName: string, firstName?: string | null): string {
  const safeName = escapeHtml(formName);
  const label = safeName ? `<strong>${safeName}</strong>` : "the form";
  const safeFirstName = firstName?.trim() ? escapeHtml(firstName.trim()) : "";
  const greeting = safeFirstName ? `Hi ${safeFirstName},` : "Hi there,";
  return `<p>${greeting}</p>
<p>Thanks for filling out ${label}. Your completed document is attached.</p>
<p>Questions? Reach us at <a href="mailto:${FILE_DELIVERY_EMAIL_FROM}">${FILE_DELIVERY_EMAIL_FROM}</a>.</p>
<p>Best,<br>The Nowadays Team</p>`;
}

/**
 * Plain-text preview of the default PDF email body, suitable for use as a
 * textarea placeholder (no HTML tags).
 *
 * @param formName - Display name of the form
 * @param firstName - Recipient's first name (optional)
 * @returns Plain-text body for preview
 */
export function buildDefaultFileDeliveryEmailBodyPlainText(formName: string, firstName?: string | null): string {
  const label = formName.trim() ? `"${formName.trim()}"` : "the form";
  const trimmed = firstName?.trim();
  const greeting = trimmed ? `Hi ${trimmed},` : "Hi there,";
  return `${greeting}

Thanks for filling out ${label}. Your completed document is attached.

Questions? Reach us at ${FILE_DELIVERY_EMAIL_FROM}.

Best,
The Nowadays Team`;
}

/** Supabase storage bucket holding the default Nowadays email banner. */
export const FILE_DELIVERY_EMAIL_DEFAULT_BANNER_BUCKET = "global";

/** Object path inside `FILE_DELIVERY_EMAIL_DEFAULT_BANNER_BUCKET` for the default email banner. */
export const FILE_DELIVERY_EMAIL_DEFAULT_BANNER_PATH = "default_email_header.png";

/** Max width (CSS) for the default Nowadays banner image. */
export const FILE_DELIVERY_EMAIL_DEFAULT_BANNER_MAX_WIDTH = "600px";

/** Max width (CSS) for a form's custom logo when used as the email header. */
export const FILE_DELIVERY_EMAIL_FORM_LOGO_MAX_WIDTH = "400px";

/** Max height (CSS) for a form's custom logo when used as the email header. */
export const FILE_DELIVERY_EMAIL_FORM_LOGO_MAX_HEIGHT = "120px";

/** Primary accent color used for links inside the wrapped PDF email body. */
export const FILE_DELIVERY_EMAIL_PRIMARY_COLOR = "#5261EB";

/** Font stack for the wrapped PDF email body. */
export const FILE_DELIVERY_EMAIL_FONT_FAMILY = "Arial, Helvetica, sans-serif";

// ── Upload Limits ────────────────────────────────────────────────────────────

/** Maximum document template file size in bytes (10 MB). Applies to PDF and DOCX. */
export const PDF_TEMPLATE_MAX_SIZE = 10 * 1024 * 1024;

/** MIME type for PDF files. */
export const PDF_MIME_TYPE = "application/pdf";

/** MIME type for DOCX (Word) files. */
export const DOCX_MIME_TYPE = "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

/** Accepted file extensions for PDF template upload. */
export const PDF_VALID_EXTENSIONS = [".pdf"] as const;

/** Magic-number signature for PDF files (`%PDF`). */
export const PDF_MAGIC_BYTES = [0x25, 0x50, 0x44, 0x46] as const;

/** Magic-number signature for DOCX files (ZIP local file header `PK\x03\x04`). */
export const DOCX_MAGIC_BYTES = [0x50, 0x4b, 0x03, 0x04] as const;

// ── UI Constants ─────────────────────────────────────────────────────────────

/**
 * Minimum width (in page-normalized 0-1 units) of a drawn field rectangle to
 * be committed as a binding. Anything narrower is treated as a stray click.
 */
export const PDF_FIELD_MIN_WIDTH = 0.02;

/** Minimum height (page-normalized 0-1 units) of a drawn field rectangle. */
export const PDF_FIELD_MIN_HEIGHT = 0.01;

/** Minimum width below which the in-progress drawing preview is not rendered. */
export const PDF_FIELD_PREVIEW_MIN_WIDTH = 0.01;

/** Minimum height below which the in-progress drawing preview is not rendered. */
export const PDF_FIELD_PREVIEW_MIN_HEIGHT = 0.005;

/** Debounce delay (ms) for saving PDF field mappings. */
export const PDF_MAPPING_SAVE_DEBOUNCE_MS = 800;

/** Debounce delay (ms) for PDF preview updates. */
export const PDF_PREVIEW_DEBOUNCE_MS = 800;

/** Signed URL expiry in seconds (1 hour). */
export const PDF_SIGNED_URL_EXPIRY = 3600;

/** TTL for public filled-PDF download tokens (30 minutes). */
export const FILLED_FILE_DOWNLOAD_TOKEN_TTL_MS = 30 * 60 * 1000;

/** Poll interval (ms) for the public filled-PDF status endpoint. */
export const FILLED_FILE_POLL_INTERVAL_MS = 2000;

/** Maximum number of times the FormModal polls for a filled PDF before giving up. */
export const FILLED_FILE_POLL_MAX_ATTEMPTS = 30;

// ── Signature Rendering ──────────────────────────────────────────────────────

/** Font family for typed signatures. */
export const SIGNATURE_FONT_FAMILY = "Dancing Script";

/** Font size for typed signatures (px). */
export const SIGNATURE_FONT_SIZE = 48;

/** Stroke color for drawn signatures. */
export const SIGNATURE_STROKE_COLOR = "#000000";

/** Stroke width for drawn signatures. */
export const SIGNATURE_STROKE_WIDTH = 2;

/** Debounce delay (ms) before uploading drawn signature. */
export const SIGNATURE_UPLOAD_DEBOUNCE_MS = 800;

// ── DOCX Fill Defaults ───────────────────────────────────────────────────────

/** Max rendered width (px) of a signature image embedded in a filled DOCX. */
export const DOCX_SIGNATURE_MAX_WIDTH_PX = 180;

/** Canvas width (px) used when rasterizing a typed signature for DOCX embedding. */
export const DOCX_TYPED_SIGNATURE_WIDTH_PX = 600;

/** Canvas height (px) used when rasterizing a typed signature for DOCX embedding. */
export const DOCX_TYPED_SIGNATURE_HEIGHT_PX = 150;

// ── PDF Fill Defaults ────────────────────────────────────────────────────────

/** Default font size for flat PDF text overlays. */
export const PDF_DEFAULT_FONT_SIZE = 12;

/** Minimum font size when shrink-to-fit kicks in for flat PDF text overlays. */
export const PDF_MIN_FONT_SIZE = 9;

/** Inset used when fitting centered text inside flat PDF field rectangles. */
export const PDF_TEXT_BOX_PADDING = 2;

/** Line-height multiplier for wrapped flat PDF text overlays. */
export const PDF_TEXT_LINE_HEIGHT_MULTIPLIER = 1.1;

/** Font-size decrement used when fitting flat PDF text overlays. */
export const PDF_TEXT_FONT_SIZE_FIT_STEP = 0.5;

/**
 * Fraction of font size treated as descender depth below the text baseline.
 * Used to center an extracted text run's box on the pdfjs baseline.
 */
export const PDF_TEXT_DESCENDER_RATIO = 0.25;

/** Font family for PDF text overlays. */
export const PDF_TEXT_FONT_FAMILY = "Helvetica";

// ── Validation ───────────────────────────────────────────────────────────────

/** Minimum PDF file size to be valid (1 KB). */
export const PDF_MIN_FILE_SIZE = 1024;

/** Maximum number of pages we'll process in a single PDF. */
export const PDF_MAX_PAGES = 100;

// ── Routes ───────────────────────────────────────────────────────────────────

/** revalidatePath target for form builder. */
export const FORM_BUILDER_PATH = "/form/[form_id]/edit";

/** revalidatePath target for form responses table. */
export const FORM_RESPONSES_PATH = "/form/[form_id]/manage";
